import React from 'react';
import { Check, Copy } from 'react-feather';
import { Highlight, themes } from 'prism-react-renderer';
import { onlyText } from 'react-children-utilities';

import Heading from './reusable/Heading';
import Paragraph from './reusable/Paragraph';
import List from './reusable/List';
import Link from './reusable/Link';
import { pushCopyCodeEvent } from '../utils/analytics';
import { useAppContext } from './AppContext';
import { Theme } from '../utils/utils';

const Blockquote = (props: React.HTMLProps<HTMLQuoteElement>) => (
  <blockquote className="text-gray-500 border-l-4 m-4 pl-4" {...props}>
    {props.children}
  </blockquote>
);

const ImgContainer = (props: React.HTMLProps<HTMLDivElement>) => (
  <div
    className="flex justify-center items-center py-4 md-img-container"
    {...props}
  >
    {props.children}
  </div>
);

interface PreCopyButtonProps {
  content: string;
}

const PreCopyButton = (props: PreCopyButtonProps) => {
  const [copied, setCopied] = React.useState(false);

  const onClick = () => {
    if (typeof navigator === 'undefined') return;

    navigator.clipboard.writeText(props.content).then(() => {
      setCopied(true);
      pushCopyCodeEvent(props.content);
    });
  };

  return (
    <button
      onClick={onClick}
      className={`bg-inherit rounded p-1 hover:bg-gray-200 active:bg-gray-300`}
    >
      {(copied && <Check size={20} />) || <Copy size={20} />}
    </button>
  );
};

const Pre = (props: React.HTMLProps<HTMLPreElement>) => {
  const { theme } = useAppContext();
  const [codeTheme, setCodeTheme] = React.useState(themes.github);

  const singleChild = React.Children.only(props.children) as React.ReactElement;

  const className = singleChild.props.className || '';
  const matches = className.match(/language-(?<lang>.*)/);

  const lang =
    matches && matches.groups && matches.groups.lang ? matches.groups.lang : '';

  const code = onlyText(props.children).trim();

  React.useEffect(() => {
    setCodeTheme(theme === Theme.DARK ? themes.vsDark : themes.github);
  }, [theme]);

  return (
    <Highlight code={code} language={lang} theme={codeTheme}>
      {({ className, style, tokens, getLineProps, getTokenProps }) => (
        <div className="flex flex-row w-full p-[20px]" style={{ ...style }}>
          <div className="overflow-auto box-border flex-1">
            <pre
              className={`${className} overflow-auto w-fit mb-4`}
              style={{ ...style }}
            >
              {tokens.map((line, i) => (
                <div key={i} {...getLineProps({ line, key: i })}>
                  {line.map((token, key) => (
                    <span key={key} {...getTokenProps({ token, key })} />
                  ))}
                </div>
              ))}
            </pre>
          </div>
          <div style={{ backgroundColor: 'inherit' }}>
            <PreCopyButton content={code} />
          </div>
        </div>
      )}
    </Highlight>
  );
};

export const MainContent = (props: React.HTMLProps<HTMLDivElement>) => (
  <article className="px-8" {...props}>
    {props.children}
  </article>
);

const Image = (props: React.HTMLProps<HTMLImageElement>) => (
  <ImgContainer>
    <img {...props} loading="lazy" />
  </ImgContainer>
);

const PostLink = (props: React.HTMLProps<HTMLAnchorElement>) => (
  <Link.Paragraph {...props} target="_blank">
    {props.children}
  </Link.Paragraph>
);

const Code = (props: React.HTMLProps<HTMLSpanElement>) => (
  <code
    className="text-red-400 dark:text-red-300 bg-gray-100 dark:bg-gray-700 px-2 whitespace-pre-wrap overflow-x-hidden break-words"
    {...props}
  >
    {props.children}
  </code>
);

export const components = {
  a: PostLink,
  blockquote: Blockquote,
  code: Code,
  h1: Heading.H1,
  h2: Heading.H2,
  h3: Heading.H3,
  h4: Heading.H4,
  h5: Heading.H5,
  img: Image,
  li: List.Item,
  ol: List.Ordered,
  p: Paragraph.Markdown,
  pre: Pre,
  ul: List.Unordered,
};
